import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import config from '../config.js';

// MUI Icons
import { Download as DownloadIcon } from '@mui/icons-material';
import { IconButton } from "@mui/material";
import { apiErrorMessage } from '../utils/apiError';
import { shopToday } from '../utils/shopDate';

const API_BASE = config.API_BASE_URL;

function formatCurrency(val) {
  if (val === null || val === undefined || val === "" || isNaN(val)) return "$0.00";
  return "$" + Number(val).toFixed(2);
}

// Friday before a Monday week-start (ISO date string math in UTC to dodge TZ drift).
// This is the suggested delivery day: material on the floor when the cut week opens.
function fridayBefore(isoMonday) {
  if (!isoMonday) return null;
  const d = new Date(isoMonday + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - 3);
  return d.toISOString().slice(0, 10);
}

function fmtShortDate(isoDate) {
  if (!isoDate) return "";
  return new Date(isoDate + "T00:00:00Z").toLocaleDateString(undefined, { month: "numeric", day: "numeric", timeZone: "UTC" });
}

// Click-to-trace highlight palette: soft row tints + stronger accents, chosen
// to stay distinct from the existing state colors (Round-5 green on To Order,
// override amber on the date input).
const HIGHLIGHT_STYLES = [
  { bg: "#e3f2fd", accent: "#1976d2" }, // blue
  { bg: "#f3e5f5", accent: "#8e24aa" }, // purple
  { bg: "#e0f2f1", accent: "#00897b" }, // teal
  { bg: "#fff3e0", accent: "#ef6c00" }, // orange
  { bg: "#fce4ec", accent: "#d81b60" }, // pink
  { bg: "#f1f8e9", accent: "#7cb342" }, // green
];

// Merge lines of the SAME material landing in the SAME (possibly overridden)
// week into one row: quantities combine, `merged_from` records every original
// forecast bucket so week edits on the merged row write an override per
// origin (and therefore replay correctly on refetch). Context columns
// (on-hand, inbound, EOW, floor, week-load) keep the first row's values —
// the earliest bucket in fetch order, i.e. the real target week when
// collapsing later weeks into an earlier one.
function lineOrigins(line) {
  return (line.merged_from && line.merged_from.length)
    ? line.merged_from
    : [{ original_week_start: line.original_week_start || line.week_start, sheets: line.sheets_required, needed: line.originalNeeded ?? line.needed_to_order }];
}
function mergeLikeLines(arr) {
  const map = new Map();
  const out = [];
  arr.forEach(line => {
    const key = `${line.material_id}|${line.week_start}`;
    const prev = map.get(key);
    if (!prev) {
      const copy = { ...line, merged_from: lineOrigins(line) };
      map.set(key, copy);
      out.push(copy);
    } else {
      prev.sheets_required = (Number(prev.sheets_required) || 0) + (Number(line.sheets_required) || 0);
      prev.toOrder = (parseInt(prev.toOrder, 10) || 0) + (parseInt(line.toOrder, 10) || 0);
      prev.originalNeeded = (Number(prev.originalNeeded) || 0) + (Number(line.originalNeeded) || 0);
      prev.selected = prev.selected || line.selected;
      ["in_flight_sheets", "backlog_sheets", "early_cut_sheets", "due_risk_sheets"].forEach(k => {
        prev[k] = (Number(prev[k]) || 0) + (Number(line[k]) || 0);
      });
      prev.merged_from = [...prev.merged_from, ...lineOrigins(line)];
    }
  });
  return out;
}

// 1. Accept the new prop `onCreateRFQ`
export default function WeeklyBuyPlanCard({ onCreateRFQ }) {
  const today = new Date();
  const nextMonth = new Date();
  nextMonth.setDate(today.getDate() + 28);
  const iso = d => d.toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(iso(today));
  const [endDate, setEndDate] = useState(iso(nextMonth));
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [creatingPO, setCreatingPO] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedVendorFilter, setSelectedVendorFilter] = useState("ALL");
  // Overdue inbound POs (ETA in the past, not received) collected from the
  // FINAL_SUM rows — surfaced as a card-level banner even when the affected
  // material has no buy row in the visible window.
  const [overdueInbound, setOverdueInbound] = useState([]);

  // Click-to-trace: material_id -> palette index. Clicking a material's code
  // or description toggles a row tint on EVERY occurrence of that material
  // across all weeks (and the Order Summary). Several materials can be lit
  // at once, each in its own color. Session-only visual aid.
  const [highlightedMats, setHighlightedMats] = useState({});
  const toggleHighlight = (materialId) => {
    if (materialId == null) return;
    setHighlightedMats(prev => {
      const next = { ...prev };
      if (next[materialId] !== undefined) {
        delete next[materialId];
      } else {
        const used = new Set(Object.values(next));
        let i = 0;
        while (used.has(i)) i++;
        next[materialId] = i;
      }
      return next;
    });
  };
  const highlightStyle = (materialId) => {
    const idx = highlightedMats[materialId];
    return idx === undefined ? null : HIGHLIGHT_STYLES[idx % HIGHLIGHT_STYLES.length];
  };
  const highlightCount = Object.keys(highlightedMats).length;
  
  // --- NEW STATE: Rounding Toggle ---
  const [roundToFive, setRoundToFive] = useState(false);

  // Manual week_start overrides (Map: `${material_id}|${original_week_start}` →
  // new week_start). Persisted in localStorage so a page refresh or any
  // re-fetch (start/end date change, Round-to-5 toggle, Apply button) replays
  // the operator's manual delivery-week edits instead of clobbering them with
  // the freshly-computed forecast buckets. Pre-2026-05-20 fix: manual edits
  // lived only in component state and were wiped by any of the above actions,
  // so an RFQ generated after such a re-fetch silently used auto-suggested
  // dates rather than the operator's pinned weeks.
  const [manualWeekOverrides, setManualWeekOverrides] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('buyPlanWeekOverrides') || '{}');
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try { localStorage.setItem('buyPlanWeekOverrides', JSON.stringify(manualWeekOverrides)); } catch {}
  }, [manualWeekOverrides]);
  // Ref mirror — lets fetchPlan read the latest overrides without listing
  // manualWeekOverrides in its useCallback deps (which would cycle the
  // useEffect that re-fetches on dependency change).
  const overridesRef = useRef(manualWeekOverrides);
  useEffect(() => { overridesRef.current = manualWeekOverrides; }, [manualWeekOverrides]);
  // Identity helper — used by handleWeekChange + replay logic.
  const overrideKey = (materialId, origWeek) => `${materialId}|${origWeek}`;

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setSuccessMsg("");
    try {
      const res = await fetch(`${API_BASE}/reports/weekly_buy_plan_with_vendors?start_date=${startDate}&end_date=${endDate}`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const rawData = await res.json();

      // Card-level overdue-inbound warning: FINAL_SUM rows carry the total
      // overdue qty per material regardless of whether any buy row is visible.
      setOverdueInbound(
        rawData
          .filter(row => row.week_start === "FINAL_SUM" && (row.overdue_inbound_qty || 0) > 0)
          .map(row => ({
            material_code: row.material_code,
            qty: row.overdue_inbound_qty,
          }))
      );

      const weekLines = rawData.filter(
        row => row.week_start !== "FINAL_SUM" && row.needed_to_order > 0
      );

      const processedLines = weekLines.map(line => {
        const vendors = line.vendors || [];
        const selectedVendor = vendors.length ? vendors[0].vendor_id : "";
        const costObj = vendors.find(v => v.vendor_id === selectedVendor);

        // --- NEW: Apply Rounding Logic on Fetch if enabled ---
        let qtyToOrder = line.needed_to_order;
        if (roundToFive) {
            qtyToOrder = Math.ceil(qtyToOrder / 5) * 5;
        }

        // Replay manual week_start override (if any) on top of the freshly
        // computed forecast bucket. original_week_start is the snapshot of
        // what the backend returned this fetch; manual edits later flip
        // week_start to the override value but leave original_week_start
        // alone so the override key remains stable across refetches.
        // Read via overridesRef so fetchPlan's closure stays stable (we
        // don't want manualWeekOverrides in its useCallback deps — that
        // would re-trigger the useEffect re-fetch on every edit).
        const origWeek = line.week_start;
        const overridden = overridesRef.current[overrideKey(line.material_id, origWeek)];

        return {
          ...line,
          vendors,
          selectedVendor,
          cost: costObj?.cost_per_unit ?? "",
          selected: true,
          toOrder: qtyToOrder,
          originalNeeded: line.needed_to_order, // Keep track of raw value
          original_week_start: origWeek,        // snapshot for override identity
          week_start: overridden || origWeek,   // apply override if present
        };
      });

      // Combine same-material lines that land in the same week (after
      // override replay) into single rows with summed quantities.
      setLines(mergeLikeLines(processedLines));
      // Reset filter on new fetch if desired, or keep current
      // setSelectedVendorFilter("ALL");
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, roundToFive]); // Added roundToFive dependency so it refetches/recalcs if checked

  const handleExportCsv = async () => {
    try {
      const response = await fetch(`${API_BASE}/reports/weekly_sheet_order_plan?start_date=${startDate}&end_date=${endDate}`);
      if (!response.ok) throw new Error('Failed to fetch data for export.');
      
      const data = await response.json();
      const dataToExport = data.filter(row => row.week_start !== 'FINAL_SUM' && row.needed_to_order > 0);

      if (dataToExport.length === 0) {
        alert("No data to export for the selected date range.");
        return;
      }

      const headers = ["Due Week Of", "Deliver By", "Order By", "Vendor", "Material Code", "Description", "Sheets Required", "On Hand", "Inbound", "Needed to Order", "EOW On Hand", "Floor Used", "Week Laser Hours (All Mats)", "Week Overflow Hours", "Early-Cut Sheets", "Past-Due Sheets"];
      const csvRows = [
        headers.join(','),
        ...dataToExport.map(row => [
          row.week_start,
          row.suggested_delivery_date || '',
          row.order_by_date || '',
          `"${(row.vendor_name || '').replace(/"/g, '""')}"`,
          `"${(row.material_code || '').replace(/"/g, '""')}"`,
          `"${(row.material_description || '').replace(/"/g, '""')}"`,
          row.sheets_required,
          row.on_hand,
          row.inbound,
          row.needed_to_order,
          row.projected_eow_on_hand ?? '',
          row.floor_used ?? '',
          row.week_load_hours ?? '',
          row.week_overflow_hours ?? '',
          row.early_cut_sheets ?? '',
          row.due_risk_sheets ?? ''
        ].join(','))
      ];
      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `Weekly_Material_Plan_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert("Failed to export data. Please try again.");
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // --- NEW: Toggle Handler for Rounding ---
  const handleToggleRounding = (e) => {
    const isChecked = e.target.checked;
    setRoundToFive(isChecked);

    setLines(prevLines => prevLines.map(line => {
        let newQty = line.toOrder;

        if (isChecked) {
            // Apply Rounding
            const current = Number(line.toOrder) || 0;
            if (current > 0) {
                newQty = Math.ceil(current / 5) * 5;
            }
        } else {
            // Optional: Revert to original calculated need when unchecked? 
            // Or just leave as is. User can click "Fetch Plan" to reset fully.
            // For now, let's restore the original raw need if available to be helpful.
            if (line.originalNeeded) {
                newQty = line.originalNeeded;
            }
        }

        return { ...line, toOrder: newQty };
    }));
  };

  const handleVendorChange = (idx, newVendorId) => {
    setLines(prevLines =>
      prevLines.map((line, i) => {
        if (i !== idx) return line;
        const vObj = line.vendors.find(v => String(v.vendor_id) === String(newVendorId));
        return {
          ...line,
          selectedVendor: newVendorId,
          cost: vObj?.cost_per_unit ?? "",
        };
      })
    );
  };

  // --- Handlers for Editable Fields ---
  const handleQtyChange = (idx, val) => setLines(prev => prev.map((line, i) => i !== idx ? line : { ...line, toOrder: val }));
  const handleCostChange = (idx, val) => setLines(prev => prev.map((line, i) => i !== idx ? line : { ...line, cost: val }));
  const handleCheck = (idx, checked) => setLines(prev => prev.map((line, i) => i !== idx ? line : { ...line, selected: checked }));
  
  // Handler for modifying the Week Start Date.
  //
  // Records the change in `manualWeekOverrides` for EVERY original forecast
  // bucket the row represents (merged rows carry several via merged_from) so
  // the edit replays when fetchPlan re-runs. Clearing back to an origin's own
  // week removes that origin's override. After the move, re-merge so the row
  // combines with any same-material row already in the target week.
  const handleWeekChange = (idx, val) => {
    const line = lines[idx];
    if (!line || !val) return;
    const origins = lineOrigins(line);
    setManualWeekOverrides(prevOverrides => {
      const next = { ...prevOverrides };
      origins.forEach(o => {
        const key = overrideKey(line.material_id, o.original_week_start);
        if (val === o.original_week_start) delete next[key];
        else next[key] = val;
      });
      return next;
    });
    setLines(prev => mergeLikeLines(prev.map((l, i) => i === idx ? { ...l, week_start: val } : l)));
  };

  // Bulk collapse: move EVERY line in a week group to another week (Mike's
  // "fold light trailing weeks into a heavier buy week" workflow). Same
  // override bookkeeping as the per-row edit, then re-merge.
  const handleMoveWeekGroup = (fromWeek, toWeek) => {
    if (!toWeek || fromWeek === toWeek) return;
    const movers = lines.filter(l => l.week_start === fromWeek);
    if (!movers.length) return;
    setManualWeekOverrides(prevOverrides => {
      const next = { ...prevOverrides };
      movers.forEach(line => {
        lineOrigins(line).forEach(o => {
          const key = overrideKey(line.material_id, o.original_week_start);
          if (toWeek === o.original_week_start) delete next[key];
          else next[key] = toWeek;
        });
      });
      return next;
    });
    setLines(prev => mergeLikeLines(prev.map(l => l.week_start === fromWeek ? { ...l, week_start: toWeek } : l)));
  };

  // Manual reset — drop ALL operator week_start overrides + re-fetch fresh
  // forecast dates. Useful when the buy plan demand shape has materially
  // changed and the operator wants to start clean.
  const handleResetOverrides = () => {
    const count = Object.keys(manualWeekOverrides).length;
    if (count === 0) return;
    if (!window.confirm(`Clear ${count} manual delivery-week override(s) and re-fetch fresh forecast dates?`)) return;
    setManualWeekOverrides({});
    // setManualWeekOverrides triggers the localStorage write via useEffect
    // above; once it lands fetchPlan will see an empty override map.
    fetchPlan();
  };

  const overrideCount = Object.keys(manualWeekOverrides).length;

  const filteredLines = useMemo(() => {
    return lines
      .map((line, index) => ({ ...line, originalIndex: index }))
      .filter(line => selectedVendorFilter === "ALL" || String(line.selectedVendor) === String(selectedVendorFilter));
  }, [lines, selectedVendorFilter]);

  // --- NEW: Group lines by week_start for display ---
  const groupedLines = useMemo(() => {
    const groups = {};
    filteredLines.forEach(line => {
        const dateKey = line.week_start || "Unknown";
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(line);
    });
    
    // Sort keys (dates)
    return Object.keys(groups).sort().map(date => ({
        date,
        items: groups[date]
    }));
  }, [filteredLines]);

  const handleSelectAll = (checked) => {
    const visibleIndices = filteredLines.map(l => l.originalIndex);
    setLines(currentLines =>
      currentLines.map((line, index) => {
        if (visibleIndices.includes(index)) {
          return { ...line, selected: checked };
        }
        return line;
      })
    );
  };

  // 2. New Handler for RFQ Bundle — preserves per-week lines for delivery schedule
  const handleCreateRFQ = () => {
    const selectedLines = filteredLines.filter(l => l.selected);
    if (selectedLines.length === 0) {
        alert("Please select rows to bundle.");
        return;
    }

    // Send each week's line individually so the RFQ PDF shows the weekly delivery
    // schedule. Delivery = the Friday BEFORE the cut week (material on the floor
    // when the week opens), matching the PO path below.
    const rfqItems = selectedLines.map(line => ({
        item_type: "Material",
        material_id: line.material_id,
        quantity_ordered: Math.max(1, parseInt(line.toOrder, 10) || 0),
        cost_per_unit: null,
        requested_delivery_date: fridayBefore(line.week_start) || line.week_start
    }));

    if (onCreateRFQ) {
        onCreateRFQ(rfqItems);
    }
  };

  const groupLinesForPOs = (linesToProcess) => {
    const map = {};
    linesToProcess.forEach(line => {
      if (!line.selected || !line.selectedVendor || !line.toOrder || Number(line.toOrder) <= 0) return;
      
      // Grouping key now effectively uses the *editable* week_start
      const key = `${line.week_start}|${line.selectedVendor}`;
      
      if (!map[key]) {
        const vendor = line.vendors.find(v => String(v.vendor_id) === String(line.selectedVendor));
        map[key] = { vendor_id: line.selectedVendor, vendor_name: vendor?.vendor_name || 'Unknown', week_start: line.week_start, lines: [] };
      }
      map[key].lines.push(line);
    });
    return Object.values(map);
  };

  const handleCreatePOs = async () => {
    setCreatingPO(true);
    setSuccessMsg("");
    try {
      const groups = groupLinesForPOs(filteredLines);
      if (!groups.length) {
        alert("No rows selected in the current view!");
        setCreatingPO(false);
        return;
      }
      for (const group of groups) {
        const payload = {
          supplier: group.vendor_name,
          order_date: iso(new Date()),
          // Deliver the Friday BEFORE the due week — material in house a week ahead of the dues
          estimated_receive_date: fridayBefore(group.week_start) || group.week_start,
          notes: `Weekly sheet buy for jobs due week of ${group.week_start}`,
          lines: group.lines.map(l => ({
            item_type: "Material",
            material_id: l.material_id,
            quantity_ordered: Math.max(1, parseInt(l.toOrder, 10) || 0),
            cost_per_unit: isNaN(Number(l.cost)) ? null : Number(l.cost),
          })),
        };
        const res = await fetch(`${API_BASE}/purchase_orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await apiErrorMessage(res));
      }
      setSuccessMsg(`Successfully created ${groups.length} PO(s)!`);
      fetchPlan();
    } catch (err) {
      setFetchError("Error creating POs: " + err.message);
    } finally {
      setCreatingPO(false);
    }
  };

  const totalCostOfSelected = useMemo(() => {
    return filteredLines.reduce((total, line) => {
      if (line.selected) {
        const quantity = Number(line.toOrder) || 0;
        const cost = Number(line.cost) || 0;
        return total + (quantity * cost);
      }
      return total;
    }, 0);
  }, [filteredLines]);

  // Per-material rollup of selected lines — collapses each material_code across all
  // weeks/vendors into a single row so the operator sees total quantity to order
  // and a per-material subtotal that sums to totalCostOfSelected.
  const materialSummary = useMemo(() => {
    const map = new Map();
    filteredLines.forEach(line => {
      if (!line.selected) return;
      const qty = Number(line.toOrder) || 0;
      if (qty <= 0) return;
      const cost = Number(line.cost) || 0;
      const key = line.material_code || `id:${line.material_id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          material_id: line.material_id,
          material_code: line.material_code,
          material_description: line.material_description,
          total_qty: 0,
          total_cost: 0,
          cost_min: cost,
          cost_max: cost,
          vendors: new Map(),
          week_count: 0,
        });
      }
      const row = map.get(key);
      row.total_qty += qty;
      row.total_cost += qty * cost;
      row.cost_min = Math.min(row.cost_min, cost);
      row.cost_max = Math.max(row.cost_max, cost);
      const vendor = line.vendors.find(v => String(v.vendor_id) === String(line.selectedVendor));
      if (vendor) {
        row.vendors.set(vendor.vendor_id, vendor.vendor_name || vendor.name || `#${vendor.vendor_id}`);
      }
      row.week_count += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.total_cost - a.total_cost);
  }, [filteredLines]);

  const summaryTotals = useMemo(() => ({
    qty: materialSummary.reduce((s, m) => s + m.total_qty, 0),
    cost: materialSummary.reduce((s, m) => s + m.total_cost, 0),
  }), [materialSummary]);

  const uniqueVendorsForFilter = useMemo(() => {
    const allVendors = new Map();
    lines.forEach(line => {
      const vendor = line.vendors.find(v => String(v.vendor_id) === String(line.selectedVendor));
      if (vendor && !allVendors.has(vendor.vendor_id)) {
        allVendors.set(vendor.vendor_id, vendor.vendor_name || vendor.name);
      }
    });
    return Array.from(allVendors.entries())
      .map(([id, name]) => ({ vendor_id: id, vendor_name: name }))
      .sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));
  }, [lines]);

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center">
        <h5 className="mb-0 flex-grow-1">Weekly Buy Plan</h5>
        <span style={{ fontSize: 14, marginLeft: 16, color: "#666" }}>
          Sheets by <strong>job due week</strong> · deliver the Friday before · ⚡ flags mark weeks over laser capacity
        </span>
        <IconButton onClick={handleExportCsv} title="Export Full Plan to CSV" disabled={loading}>
          <DownloadIcon />
        </IconButton>
      </div>
      <div className="card-body">
        <div className="row align-items-end mb-3">
          <div className="col-md-2">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-control" value={startDate} onChange={e => setStartDate(e.target.value)} disabled={loading} />
          </div>
          <div className="col-md-2">
            <label className="form-label">End Date</label>
            <input type="date" className="form-control" value={endDate} onChange={e => setEndDate(e.target.value)} disabled={loading} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Filter by Vendor</label>
            <select className="form-select" value={selectedVendorFilter} onChange={e => setSelectedVendorFilter(e.target.value)} disabled={loading || lines.length === 0}>
              <option value="ALL">Show All Vendors</option>
              {uniqueVendorsForFilter.map(v => (<option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>))}
            </select>
          </div>
          <div className="col-md-2 d-flex align-items-end gap-2">
            <button className="btn btn-primary" onClick={fetchPlan} disabled={loading}>
              {loading ? "Loading..." : "Fetch Plan"}
            </button>
          </div>
          <div className="col-md-3 text-end">
            {overrideCount > 0 && (
              <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
                <span className="badge bg-warning text-dark" title="Manual delivery-week overrides survive refresh, date-range changes, and Round-to-5 toggles. They're saved to your browser's local storage.">
                  ✎ {overrideCount} manual week override{overrideCount === 1 ? "" : "s"}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning"
                  onClick={handleResetOverrides}
                  title="Clear all manual delivery-week overrides and re-fetch fresh forecast dates"
                >
                  Reset
                </button>
              </div>
            )}
            {successMsg && <div className="alert alert-success py-2 px-3 mb-0">{successMsg}</div>}
            {fetchError && <div className="alert alert-danger py-2 px-3 mb-0">{fetchError}</div>}
          </div>
        </div>

        {overdueInbound.length > 0 && (
          <div className="alert alert-warning py-2 px-3 mb-3">
            <strong>⚠ Overdue inbound:</strong>{" "}
            {overdueInbound.map(o => `${o.material_code} (${o.qty} sheets)`).join(" · ")}
            <span className="ms-2 small text-muted">
              PO ETA is in the past and not received — counted as arriving this week; chase the vendor if not on a truck.
            </span>
          </div>
        )}

        <div className="mb-3">
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowInstructions(!showInstructions)}>
              {showInstructions ? "Hide" : "Show"} Instructions
            </button>
            {highlightCount > 0 && (
              <button className="btn btn-sm btn-outline-info" onClick={() => setHighlightedMats({})} title="Remove all material trace highlights">
                ✕ Clear {highlightCount} highlight{highlightCount === 1 ? "" : "s"}
              </button>
            )}
          </div>
          {showInstructions && (
            <div className="card card-body mt-2" style={{fontSize: '0.9em'}}>
              <ol>
                <li><strong>Select Dates:</strong> Choose a start and end date for the planning period and click "Fetch Plan".</li>
                <li><strong>How weeks work:</strong> Each group is a <strong>job due week</strong>. Suggested delivery is the <strong>Friday before</strong> — material in house about a week ahead of the (mostly Friday) due dates, leaving the due week for normal processing. "Order by" backs off the vendor lead time from that Friday. The plan never schedules build-ahead for you — a ⚡ badge flags weeks whose total due-load exceeds laser capacity so you can choose what to pre-stage by editing Week Start on specific rows.</li>
                <li><strong>Filter & Review:</strong> Use the vendor dropdown to focus on one supplier. Red/yellow chips flag lines whose order-by date is past or imminent; ⚠ on Inbound marks overdue POs. <strong>Click any Material or Description</strong> to highlight every week of that material (click again — or "Clear highlights" — to remove; several can be lit at once in different colors).</li>
                <li><strong>Export (Optional):</strong> Click the download icon in the header to get a full CSV of the plan.</li>
                <li><strong>Adjust:</strong> Modify the Week Start, Vendor, Cost, and Quantity in the table as needed. Use 'Round to 5' to bulk adjust quantities.</li>
                <li><strong>Select Rows:</strong> Use the checkboxes to select lines for purchase orders. PO/RFQ groups one delivery per vendor per week automatically.</li>
                <li><strong>Create PO(s):</strong> Click "Create PO(s)" to commit immediately, OR "Create RFQ Bundle" to bid.</li>
              </ol>
            </div>
          )}
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="table table-sm table-bordered align-middle">
            <thead>
              <tr>
                <th><input type="checkbox" checked={filteredLines.length > 0 && filteredLines.every(l => l.selected)} onChange={e => handleSelectAll(e.target.checked)} disabled={loading || filteredLines.length === 0} /></th>
                <th title="The week the jobs are DUE. Deliveries are suggested for the Friday before, so material is in house about a week ahead of the due dates.">Due Week</th>
                <th>Material</th><th>Description</th><th>Vendor</th><th>Cost/Unit</th><th>On Hand</th><th>Inbound</th><th>Sheets Needed</th>
                <th title="Projected end-of-week on hand after this buy / safety floor used (max of material minimum and 2 cutting-days of near-term burn)">EOW / Floor</th>
                {/* MODIFIED HEADER WITH CHECKBOX */}
                <th style={{ backgroundColor: roundToFive ? "#e8f5e9" : "inherit" }}>
                    <div className="d-flex align-items-center gap-1">
                        To Order
                        <div className="form-check form-switch ms-1" title="Round quantities up to nearest 5">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="roundToFiveSwitch"
                                checked={roundToFive} 
                                onChange={handleToggleRounding} 
                            />
                            <label className="form-check-label small text-muted" htmlFor="roundToFiveSwitch">
                                Round 5
                            </label>
                        </div>
                    </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (<tr><td colSpan={11} className="text-center">Loading...</td></tr>) :
               filteredLines.length === 0 ? (<tr><td colSpan={11} className="text-center text-muted">No materials need ordering for the selected criteria.</td></tr>) :

               // --- CHANGED: Render grouped sections instead of flat list ---
               groupedLines.map(group => (
                 <React.Fragment key={group.date}>
                   {/* GROUP HEADER ROW */}
                   <tr className="table-secondary">
                     <td colSpan="11" className="fw-bold ps-3">
                       <div className="d-flex align-items-center flex-wrap gap-1">
                         <span>
                           Week of {new Date(group.date).toLocaleDateString(undefined, { timeZone: 'UTC' })} <span className="fw-normal text-muted">(jobs due)</span>
                           <span className="fw-normal text-muted ms-2">
                             → deliver by <strong>Fri {fmtShortDate(fridayBefore(group.date))}</strong>
                             {(() => {
                               // One consolidated drop per vendor per week: summarize order-by per vendor in this group
                               const byVendor = {};
                               group.items.forEach(l => {
                                 const v = l.vendors?.find(x => String(x.vendor_id) === String(l.selectedVendor));
                                 const name = v?.vendor_name || l.vendor_name || "vendor";
                                 if (l.order_by_date && (!byVendor[name] || l.order_by_date < byVendor[name])) byVendor[name] = l.order_by_date;
                               });
                               return Object.entries(byVendor).map(([name, ob]) => (
                                 <span key={name} className="ms-2">· order {name} by <strong>{fmtShortDate(ob)}</strong></span>
                               ));
                             })()}
                           </span>
                         </span>
                         <span className="ms-auto fw-normal d-flex align-items-center gap-1">
                           <select
                             className="form-select form-select-sm"
                             style={{ width: "auto", fontSize: "0.75rem" }}
                             value=""
                             title="Fold this whole week into another buy week — same-material lines merge and quantities combine. Survives refresh (uses the manual week overrides)."
                             onChange={e => { const v = e.target.value; e.target.value = ""; if (v) handleMoveWeekGroup(group.date, v); }}
                           >
                             <option value="">⇊ Move {group.items.length} line{group.items.length === 1 ? "" : "s"} to…</option>
                             {groupedLines.filter(g => g.date !== group.date).map(g => (
                               <option key={g.date} value={g.date}>week of {fmtShortDate(g.date)} (deliver Fri {fmtShortDate(fridayBefore(g.date))})</option>
                             ))}
                           </select>
                         </span>
                       </div>
                       {(() => {
                         // Capacity flag in LASER HOURS (sheet counts are meaningless across
                         // thicknesses). Informational — Mike pre-stages via week overrides.
                         const l0 = group.items.find(l => (l.week_overflow_hours || 0) > 0);
                         if (!l0) return null;
                         return (
                           <div className="fw-normal mt-1">
                             <span className="badge bg-warning text-dark">⚡ {l0.week_load_hours}h of laser cutting due this week vs ~{l0.weekly_capacity_hours}h/wk capacity</span>
                             <span className="small text-muted ms-2">
                               ~{l0.week_overflow_hours}h must be cut earlier — if pre-staging, edit Week Start on the rows you choose to pull a week ahead.
                             </span>
                           </div>
                         );
                       })()}
                     </td>
                   </tr>
                   
                   {/* GROUP ITEMS */}
                   {group.items.map(line => {
                     const hl = highlightStyle(line.material_id);
                     return (
                      <tr key={line.originalIndex} style={hl ? { backgroundColor: hl.bg, boxShadow: `inset 4px 0 0 ${hl.accent}` } : undefined}>
                        <td><input type="checkbox" checked={line.selected} onChange={e => handleCheck(line.originalIndex, e.target.checked)} /></td>
                        
                        {/* EDITABLE WEEK START */}
                        <td>
                          {(() => {
                            const isOverridden = !!line.original_week_start &&
                              line.week_start !== line.original_week_start;
                            return (
                              <>
                                <input
                                  type="date"
                                  className={"form-control form-control-sm" + (isOverridden ? " border-warning" : "")}
                                  style={{ minWidth: 130, backgroundColor: isOverridden ? "#fff8e1" : undefined }}
                                  value={line.week_start}
                                  onChange={e => handleWeekChange(line.originalIndex, e.target.value)}
                                  title={isOverridden
                                    ? `Manually overridden. Forecast bucket was ${line.original_week_start}. Survives refresh.`
                                    : "Edit to override the forecast delivery week. Override is saved and replayed on refetch."}
                                />
                                {isOverridden && (
                                  <div className="small text-warning mt-1" style={{ fontSize: '0.7rem' }}>
                                    ✎ was {line.original_week_start}
                                  </div>
                                )}
                                {line.merged_from && line.merged_from.length > 1 && (
                                  <div
                                    className="small text-info mt-1"
                                    style={{ fontSize: '0.7rem', cursor: 'help' }}
                                    title={`Combined from forecast weeks: ${line.merged_from.map(o => `${o.original_week_start} (${o.needed ?? o.sheets} sheets)`).join(', ')}. Move or reset to split back apart.`}
                                  >
                                    ⇊ {line.merged_from.length} weeks merged
                                  </div>
                                )}
                                {(() => {
                                  // Order-by deadline chip. Recompute for overridden weeks
                                  // (delivery Friday moves with the week; lead time is per material).
                                  const lead = Number(line.lead_time_days) || 7;
                                  const delivery = fridayBefore(line.week_start);
                                  let orderBy = line.order_by_date;
                                  if (isOverridden && delivery) {
                                    const d = new Date(delivery + "T00:00:00Z");
                                    d.setUTCDate(d.getUTCDate() - lead);
                                    orderBy = d.toISOString().slice(0, 10);
                                  }
                                  if (!orderBy || !(line.needed_to_order > 0)) return null;
                                  const todayIso = shopToday();
                                  const soonIso = shopToday(new Date(Date.now() + 3 * 86400000));
                                  const status = orderBy < todayIso ? "overdue" : (orderBy <= soonIso ? "now" : "ok");
                                  if (status === "overdue") {
                                    return <div className="badge bg-danger mt-1" title={`Vendor lead time ${lead}d — ordering today may miss the Fri ${fmtShortDate(delivery)} delivery`}>ORDER NOW — past {fmtShortDate(orderBy)}</div>;
                                  }
                                  if (status === "now") {
                                    return <div className="badge bg-warning text-dark mt-1" title={`Vendor lead time ${lead}d for delivery Fri ${fmtShortDate(delivery)}`}>order by {fmtShortDate(orderBy)}</div>;
                                  }
                                  return <div className="small text-muted mt-1" style={{ fontSize: '0.7rem' }}>order by {fmtShortDate(orderBy)}</div>;
                                })()}
                              </>
                            );
                          })()}
                        </td>

                        <td
                          onClick={() => toggleHighlight(line.material_id)}
                          style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                          title={hl ? "Click to remove the highlight for this material" : "Click to highlight every week of this material"}
                        >
                          {hl && <span style={{ color: hl.accent, marginRight: 4 }}>●</span>}
                          {line.material_code}
                        </td>
                        <td
                          onClick={() => toggleHighlight(line.material_id)}
                          style={{ cursor: "pointer" }}
                          title={hl ? "Click to remove the highlight for this material" : "Click to highlight every week of this material"}
                        >
                          {line.material_description}
                        </td>
                        <td>
                          <select className="form-select form-select-sm" value={line.selectedVendor} onChange={e => handleVendorChange(line.originalIndex, e.target.value)} disabled={!line.vendors.length}>
                            {line.vendors.length === 0 ? (<option value="">-- No Vendor --</option>) :
                             (line.vendors.map(v => (<option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name || v.name}</option>)))}
                          </select>
                        </td>
                        <td><input type="number" step="0.01" className="form-control form-control-sm" style={{ minWidth: 75 }} value={line.cost ?? ""} placeholder="--" onChange={e => handleCostChange(line.originalIndex, e.target.value)} /></td>
                        <td>{line.on_hand}</td>
                        <td>
                          {line.inbound}
                          {(line.overdue_inbound_qty || 0) > 0 && (
                            <span
                              className="ms-1"
                              title={`${line.overdue_inbound_qty} sheets on overdue PO(s) ${(line.overdue_inbound_pos || []).map(p => `#${p.po_id} (ETA ${p.eta})`).join(", ")} — counted as arriving this week`}
                              style={{ cursor: "help" }}
                            >⚠</span>
                          )}
                        </td>
                        <td
                          title={`Sheets for jobs DUE this week${(line.in_flight_sheets || 0) > 0 ? ` · ${line.in_flight_sheets} on the laser now` : ""}${(line.backlog_sheets || 0) > 0 ? ` · includes ${line.backlog_sheets} backlog sheets wanted before this week` : ""}${(line.early_cut_sheets || 0) > 0 ? ` · schedule plans to cut ${line.early_cut_sheets} of these EARLIER against safety stock` : ""}`}
                          style={{ cursor: "help" }}
                        >
                          {line.sheets_required}
                          {(line.due_risk_sheets || 0) > 0 && (
                            <div className="badge bg-danger-subtle text-danger border border-danger mt-1" title={`${line.due_risk_sheets} sheets belong to tasks whose scheduled/projected cut lands past the job due date — capacity problem, check the schedule`}>
                              {line.due_risk_sheets} past due
                            </div>
                          )}
                        </td>
                        <td className="small text-muted" title="Projected end-of-week on hand after this buy / safety floor used">
                          {line.projected_eow_on_hand ?? "—"}{line.floor_used != null ? ` / ${line.floor_used}` : ""}
                        </td>
                        
                        {/* Highlight column if rounding is active */}
                        <td style={{ backgroundColor: roundToFive ? "#e8f5e9" : "inherit" }}>
                            <input 
                                type="number" 
                                min={1} 
                                step={1} 
                                className="form-control form-control-sm" 
                                style={{ minWidth: 75 }} 
                                value={line.toOrder} 
                                onChange={e => handleQtyChange(line.originalIndex, e.target.value.replace(/[^0-9]/g, ""))} 
                            />
                        </td>
                      </tr>
                     );
                   })}
                 </React.Fragment>
               ))
              }
            </tbody>
          </table>
        </div>
        
        {materialSummary.length > 0 && (
          <div className="card mt-3 border-primary">
            <div className="card-header bg-light d-flex justify-content-between align-items-center py-2">
              <h6 className="mb-0">
                Order Summary by Material
                <span className="badge bg-primary ms-2">{materialSummary.length}</span>
              </h6>
              <span className="small text-muted">Live rollup — updates as you adjust selection, quantity, cost, or vendor</span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table className="table table-sm table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Material</th>
                    <th>Description</th>
                    <th>Vendor(s)</th>
                    <th className="text-center">Weeks</th>
                    <th className="text-end">Total Qty</th>
                    <th className="text-end">Cost/Unit</th>
                    <th className="text-end">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {materialSummary.map(m => {
                    const vendorList = Array.from(m.vendors.values());
                    const hl = highlightStyle(m.material_id);
                    return (
                      <tr key={m.key} style={hl ? { backgroundColor: hl.bg, boxShadow: `inset 4px 0 0 ${hl.accent}` } : undefined}>
                        <td onClick={() => toggleHighlight(m.material_id)} style={{ cursor: "pointer" }} title={hl ? "Click to remove the highlight for this material" : "Click to highlight every week of this material"}>
                          {hl && <span style={{ color: hl.accent, marginRight: 4 }}>●</span>}
                          <code>{m.material_code}</code>
                        </td>
                        <td className="small" onClick={() => toggleHighlight(m.material_id)} style={{ cursor: "pointer" }}>{m.material_description}</td>
                        <td className="small">
                          {vendorList.length === 0
                            ? <em className="text-muted">none</em>
                            : vendorList.join(", ")}
                        </td>
                        <td className="text-center">{m.week_count}</td>
                        <td className="text-end fw-bold">{m.total_qty}</td>
                        <td className="text-end">
                          {m.cost_min === m.cost_max
                            ? formatCurrency(m.cost_min)
                            : `${formatCurrency(m.cost_min)} – ${formatCurrency(m.cost_max)}`}
                        </td>
                        <td className="text-end">{formatCurrency(m.total_cost)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="table-light fw-bold">
                  <tr>
                    <td colSpan={4} className="text-end">Grand Total</td>
                    <td className="text-end">{summaryTotals.qty}</td>
                    <td></td>
                    <td className="text-end">{formatCurrency(summaryTotals.cost)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            <button className="btn btn-success me-2" onClick={handleCreatePOs} disabled={creatingPO || loading || !filteredLines.some(l => l.selected)}>
                {creatingPO ? "Creating PO(s)..." : "Create PO(s) for Selected"}
            </button>
            {/* 3. New Button */}
            <button
                className="btn btn-outline-dark"
                onClick={handleCreateRFQ}
                disabled={creatingPO || loading || !filteredLines.some(l => l.selected)}
            >
                Create RFQ Bundle
            </button>
          </div>
          <div className="fw-bold fs-5">
            Total Cost of Selected (Visible Only): {formatCurrency(totalCostOfSelected)}
          </div>
        </div>
      </div>
    </div>
  );
}